import type { RequestOptions, RestClient } from '@directus/sdk';
import type {
  TGetDepartmentRowsRequestBody,
  TGetDepartmentRowsResponse,
} from '@sediwork/shared/types/api/distribute/getDepartmentRows';
import type { TGetFacilityTreeResponse } from '@sediwork/shared/types/api/facilityTree/getFacilityTree';
import type {
  TGetPlanningGroupResponse,
  TGetPlanningGroupsRequest,
  TGetPlanningGroupsResponse,
} from '@sediwork/shared/types/api/distribute/getPlanningGroups';
import type { TGetServiceGroupsRequest, TGetServiceGroupsResponse } from '@sediwork/shared/types/api/distribute/getServiceGroups';
import type {
  TGetUserResponse,
  TGetUsersRequest,
  TGetUsersResponse,
} from '@sediwork/shared/types/api/distribute/getUsers';
import type {
  TGetWorkloadsMapRequest,
  TGetWorkloadsMapResponse,
} from '@sediwork/shared/types/api/distribute/getWorkloads';
import type {
  TChatRequest,
  TChatResponse,
  TChatThreadDetailResponse,
  TChatThreadListResponse,
} from '@sediwork/shared/types/api/llm-chat/chat';
import type {
  TCreateQualificationBody,
  TQualificationWriteResponse,
} from '@sediwork/shared/types/api/qualifications/createQualification';
import type {
  TCreateQualificationRecordBody,
} from '@sediwork/shared/types/api/qualifications/createQualificationRecord';
import type {
  TPhysicianQualificationsResponse,
} from '@sediwork/shared/types/api/qualifications/getPhysicianQualifications';
import type {
  TQualificationCatalogResponse,
} from '@sediwork/shared/types/api/qualifications/getQualificationCatalog';
import type {
  TQualificationOverviewResponse,
} from '@sediwork/shared/types/api/qualifications/getQualificationOverview';
import type {
  TQualificationSummariesResponse,
} from '@sediwork/shared/types/api/qualifications/getQualificationSummaries';
import type { TUpdateQualificationBody } from '@sediwork/shared/types/api/qualifications/updateQualification';
import type {
  TUpdateQualificationRecordBody,
} from '@sediwork/shared/types/api/qualifications/updateQualificationRecord';
import type {
  TDateString, TEmptyObject, TUuid,
} from '@sediwork/shared/types/common';
import type { TQualificationPhysicianFilter } from '@sediwork/shared/types/qualification';

type TRequestOptionsBase = Pick<RequestOptions, 'path'> & Readonly<{
  /** Error statuses the caller handles itself, without the global error notification. */
  handledStatuses?: ReadonlyArray<number>
}>;

type TRequestGetOptions<T> = TRequestOptionsBase & Readonly<{
  method: 'GET'
  params: T
}>;

type TRequestPostOptions<T> = TRequestOptionsBase & Readonly<{
  method: 'POST'
  body: T
}>;

type TRequestPatchOptions<T> = TRequestOptionsBase & Readonly<{
  method: 'PATCH'
  body: T
}>;

type TRequestDeleteOptions = TRequestOptionsBase & Readonly<{
  method: 'DELETE'
}>;

type TRequestOptions<T> =
  | TRequestGetOptions<T>
  | TRequestPostOptions<T>
  | TRequestPatchOptions<T>
  | TRequestDeleteOptions;

type TPhysicianFilterParams = Readonly<Partial<Record<keyof TQualificationPhysicianFilter, string>>>
  & Readonly<{ facilityId: TUuid }>;

type TQualificationSummariesParams = Readonly<{
  referenceDate: TDateString
  facilityId: TUuid
}>;

type TQualificationOverviewParams = TPhysicianFilterParams & Readonly<{
  referenceDate: TDateString
  includeDeactivated?: 'true'
}>;

/* Query values are strings, and a key left out must not arrive as "undefined". */
const toPhysicianFilterParams = (
  filter: TQualificationPhysicianFilter,
): Partial<Record<keyof TQualificationPhysicianFilter, string>> => Object.fromEntries(
  Object.entries(filter)
    .filter(([, value]) => value !== undefined && value !== '')
    .map(([key, value]) => [key, String(value)]),
);

export class Api<SCHEMA> {
  constructor (private client: RestClient<SCHEMA>) {}

  private request<REQ extends object = TEmptyObject, RES = void>(
    { handledStatuses, ...options }: TRequestOptions<REQ>,
  ) {
    let body = undefined;
    const rawBody = (options.method === 'POST' || options.method === 'PATCH') && options.body;
    if (rawBody && typeof rawBody !== 'string') {
      try {
        body = JSON.stringify(rawBody);
      } catch {
        // Continue regardless of errors
      }
    }

    /* The SDK only forwards known fetch options; the interceptor in plugins/directus.js reads this. */
    const onRequest = handledStatuses
      && ((fetchOptions: RequestInit) => Object.assign(fetchOptions, { handledStatuses }));
    return this.client.request<RES>(() => ({ ...options, body, ...(onRequest && { onRequest }) }));
  }

  // ToDo: Make it a GET request once we only have primitive types as parameters
  public getInternalUsers = (
    body: TGetUsersRequest,
  ) => this.request<TGetUsersRequest, TGetUsersResponse>({
    method: 'POST',
    path: '/distribute/users/internal',
    body,
  });

  // ToDo: Make it a GET request once we only have primitive types as parameters
  public getExternalUsers = (
    body: TGetUsersRequest,
  ) => this.request<TGetUsersRequest, TGetUsersResponse>({
    method: 'POST',
    path: '/distribute/users/external',
    body,
  });

  // ToDo: Make it a GET request once we only have primitive types as parameters
  public getUser = (
    userId: TUuid,
    body: TGetUsersRequest,
  ) => this.request<TGetUsersRequest, TGetUserResponse>({
    method: 'POST',
    path: `/distribute/users/${ userId }`,
    body,
  });

  public getUserWorkloads = (
    body: TGetWorkloadsMapRequest,
  ) => this.request<TGetWorkloadsMapRequest, TGetWorkloadsMapResponse>({
    method: 'POST',
    path: '/distribute/workloads',
    body,
  });

  public getFacilityTree = () => this.request<TEmptyObject, TGetFacilityTreeResponse>({
    method: 'GET',
    path: '/distribute/facilityTree',
    params: {},
  });

  public getDepartmentRows = (
    body: TGetDepartmentRowsRequestBody,
  ) => this.request<TGetDepartmentRowsRequestBody, TGetDepartmentRowsResponse>({
    method: 'POST',
    path: '/distribute/departmentRows',
    body,
  });

  public getDepartmentRow = (
    elementId: TUuid,
    body: TGetDepartmentRowsRequestBody,
  ) => this.request<TGetDepartmentRowsRequestBody, TGetDepartmentRowsResponse>({
    method: 'POST',
    path: `/distribute/departmentRows/${elementId}`,
    body,
  });

  public getServiceGroups = (
    params: TGetServiceGroupsRequest,
  ) => this.request<TGetServiceGroupsRequest, TGetServiceGroupsResponse>({
    method: 'GET',
    path: '/distribute/servicegroups',
    params,
  });

  public getPlanningGroups = (
    params: TGetPlanningGroupsRequest,
  ) => this.request<TGetPlanningGroupsRequest, TGetPlanningGroupsResponse>({
    method: 'GET',
    path: '/distribute/planningGroups/',
    params,
  });

  public getPlanningGroup = (
    planningGroupId: TUuid,
  ) => this.request<TEmptyObject, TGetPlanningGroupResponse>({
    method: 'GET',
    path: `/distribute/planningGroups/${ planningGroupId }`,
    params: {},
  });

  public getChatCompletion = (
    body: TChatRequest,
  ) => this.request<TChatRequest, TChatResponse>({
    method: 'POST',
    path: '/llm-chat/completion',
    body,
  });

  public getChatThreads = () => this.request<TEmptyObject, TChatThreadListResponse>({
    method: 'GET',
    path: '/llm-chat/threads',
    params: {},
  });

  public getChatThread = (
    threadId: TUuid,
  ) => this.request<TEmptyObject, TChatThreadDetailResponse>({
    method: 'GET',
    path: `/llm-chat/threads/${ threadId }`,
    params: {},
  });

  public deleteChatThread = (
    threadId: TUuid,
  ) => this.request({
    method: 'DELETE',
    path: `/llm-chat/threads/${ threadId }`,
  });

  public getQualificationCatalog = () => this.request<TEmptyObject, TQualificationCatalogResponse>({
    method: 'GET',
    path: '/qualifications',
    params: {},
  });

  public createQualification = (
    body: TCreateQualificationBody,
  ) => this.request<TCreateQualificationBody, TQualificationWriteResponse>({
    method: 'POST',
    path: '/qualifications',
    body,
  });

  public updateQualification = (
    qualificationId: TUuid,
    body: TUpdateQualificationBody,
  ) => this.request<TUpdateQualificationBody, TQualificationWriteResponse>({
    method: 'PATCH',
    path: `/qualifications/${ qualificationId }`,
    body,
  });

  public getPhysicianQualifications = (
    physicianId: TUuid,
    referenceDate: TDateString,
  ) => this.request<{ referenceDate: TDateString }, TPhysicianQualificationsResponse>({
    method: 'GET',
    path: `/qualifications/physicians/${ physicianId }`,
    params: { referenceDate },
    // A physician outside the viewer's facilities: the card hides itself instead.
    handledStatuses: [403],
  });

  public getQualificationSummaries = (
    referenceDate: TDateString,
    facilityId: TUuid,
  ) => this.request<TQualificationSummariesParams, TQualificationSummariesResponse>({
    method: 'GET',
    path: '/qualifications/summary',
    params: { referenceDate, facilityId },
  });

  public getQualificationOverview = (
    referenceDate: TDateString,
    facilityId: TUuid,
    filter: TQualificationPhysicianFilter,
    includeDeactivated: boolean,
  ) => this.request<TQualificationOverviewParams, TQualificationOverviewResponse>({
    method: 'GET',
    path: '/qualifications/physicians',
    params: {
      referenceDate,
      facilityId,
      ...(includeDeactivated && { includeDeactivated: 'true' }),
      ...toPhysicianFilterParams(filter),
    },
  });

  public createQualificationRecord = (
    physicianId: TUuid,
    body: TCreateQualificationRecordBody,
  ) => this.request<TCreateQualificationRecordBody, TQualificationWriteResponse>({
    method: 'POST',
    path: `/qualifications/physicians/${ physicianId }/records`,
    body,
  });

  public updateQualificationRecord = (
    recordId: TUuid,
    body: TUpdateQualificationRecordBody,
  ) => this.request<TUpdateQualificationRecordBody, TQualificationWriteResponse>({
    method: 'PATCH',
    path: `/qualifications/records/${ recordId }`,
    body,
  });

  public deleteQualificationRecord = (
    recordId: TUuid,
  ) => this.request<TEmptyObject, TQualificationWriteResponse>({
    method: 'DELETE',
    path: `/qualifications/records/${ recordId }`,
  });
}
